import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useAuthFetch } from '../hooks/useAuthFetch';
import '../stili/AggiornaGenitore.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function AggiornaGenitore() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [categorie, setCategorie] = useState([]);
  const [categoriaSelezionata, setCategoriaSelezionata] = useState('');
  const [statoFiltro, setStatoFiltro] = useState('ATTIVO');
  const [genitori, setGenitori] = useState([]);

  const [genitoreInModifica, setGenitoreInModifica] = useState(null);

  const [formEdit, setFormEdit] = useState({
    nome: '',
    cognome: '',
    telefono: '',
    email: '',
    indirizzo: '',
    idCategoriaFiglio: '',
    stato: 'ATTIVO'
  });

  const [message, setMessage] = useState('');
  const [loadingCategorie, setLoadingCategorie] = useState(false);
  const [loadingGenitori, setLoadingGenitori] = useState(false);

  const campiMaiuscoli = ['nome', 'cognome', 'indirizzo'];

  const normalizeUpper = (value) => (value || '').toString().trim().toUpperCase();
  const normalizeEmail = (value) => (value || '').toString().trim().toLowerCase();
  const normalizePlain = (value) => (value || '').toString();

  useEffect(() => {
    const fetchCategorie = async () => {
      setLoadingCategorie(true);
      setMessage('');

      try {
        const res = await authFetch(`${API_URL}/api/admin/categorie`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        setCategorie(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Errore fetch categorie:', err);
        setCategorie([]);
        setMessage('❌ Impossibile caricare le categorie.');
      } finally {
        setLoadingCategorie(false);
      }
    };

    fetchCategorie();
  }, [authFetch]);

  const fetchGenitori = useCallback(async () => {
    setMessage('');
    setGenitoreInModifica(null);

    if (!categoriaSelezionata) {
      setGenitori([]);
      return;
    }

    setLoadingGenitori(true);

    try {
      let url = `${API_URL}/api/admin/genitori/categoria/${encodeURIComponent(categoriaSelezionata)}`;

      if (statoFiltro) {
        url += `?stato=${encodeURIComponent(statoFiltro)}`;
      }

      const res = await authFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setGenitori(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore fetch genitori:', err);
      setGenitori([]);
      setMessage('❌ Errore nel caricamento dei genitori.');
    } finally {
      setLoadingGenitori(false);
    }
  }, [authFetch, categoriaSelezionata, statoFiltro]);

  useEffect(() => {
    fetchGenitori();
  }, [fetchGenitori]);

  function handleEditClick(genitore) {
    if (!genitore) {
      setGenitoreInModifica(null);
      return;
    }

    setMessage('');
    setGenitoreInModifica(genitore.id);

    setFormEdit({
      nome: normalizeUpper(genitore.nome),
      cognome: normalizeUpper(genitore.cognome),
      telefono: normalizePlain(genitore.telefono),
      email: normalizeEmail(genitore.email),
      indirizzo: normalizeUpper(genitore.indirizzo),
      idCategoriaFiglio:
        genitore.idCategoriaFiglio !== undefined && genitore.idCategoriaFiglio !== null
          ? String(genitore.idCategoriaFiglio)
          : String(categoriaSelezionata || ''),
      stato: normalizeUpper(genitore.stato || 'ATTIVO')
    });
  }

  function handleEditChange(e) {
    const { name, value } = e.target;

    if (name === 'email') {
      setFormEdit((prev) => ({
        ...prev,
        email: normalizeEmail(value)
      }));
      return;
    }

    if (name === 'idCategoriaFiglio') {
      setFormEdit((prev) => ({
        ...prev,
        idCategoriaFiglio: value
      }));
      return;
    }

    if (campiMaiuscoli.includes(name)) {
      setFormEdit((prev) => ({
        ...prev,
        [name]: value.toUpperCase()
      }));
      return;
    }

    setFormEdit((prev) => ({
      ...prev,
      [name]: value
    }));
  }

  async function ricaricaLista() {
    await fetchGenitori();
  }

  async function handleSave(idGenitore) {
    setMessage('');

    if (!formEdit.nome.trim()) {
      setMessage('❌ Il nome è obbligatorio.');
      return;
    }

    if (!formEdit.cognome.trim()) {
      setMessage('❌ Il cognome è obbligatorio.');
      return;
    }

    if (!formEdit.email.trim()) {
      setMessage('❌ L’email è obbligatoria.');
      return;
    }

    if (!formEdit.idCategoriaFiglio) {
      setMessage('❌ Seleziona la categoria del figlio.');
      return;
    }

    try {
      const payload = {
        nome: formEdit.nome.trim().toUpperCase(),
        cognome: formEdit.cognome.trim().toUpperCase(),
        telefono: formEdit.telefono.trim() || null,
        email: formEdit.email.trim().toLowerCase(),
        indirizzo: formEdit.indirizzo.trim().toUpperCase(),
        idCategoriaFiglio: Number(formEdit.idCategoriaFiglio),
        stato: normalizeUpper(formEdit.stato || 'ATTIVO')
      };

      const res = await authFetch(`${API_URL}/api/admin/genitori/${idGenitore}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errResp = await res.json().catch(() => ({}));
        throw new Error(errResp.message || `Errore HTTP ${res.status}`);
      }

      const aggiornato = await res.json();

      setGenitori((prev) =>
        prev.map((g) => (String(g.id) === String(idGenitore) ? aggiornato : g))
      );

      setGenitoreInModifica(aggiornato.id);
      handleEditClick(aggiornato);
      setMessage('✅ Genitore aggiornato con successo.');
    } catch (err) {
      console.error('Errore nel salvataggio:', err);
      setMessage(`❌ ${err.message}`);
    }
  }

  async function handleDisattiva(idGenitore) {
    setMessage('');

    try {
      const res = await authFetch(`${API_URL}/api/admin/genitori/${idGenitore}/disattiva`, {
        method: 'PUT'
      });

      if (!res.ok) {
        const errResp = await res.json().catch(() => ({}));
        throw new Error(errResp.message || `Errore HTTP ${res.status}`);
      }

      setGenitoreInModifica(null);
      await ricaricaLista();
      setMessage('✅ Genitore disattivato.');
    } catch (err) {
      console.error('Errore disattivazione:', err);
      setMessage(`❌ ${err.message}`);
    }
  }

  async function handleRiattiva(idGenitore) {
    setMessage('');

    try {
      const res = await authFetch(`${API_URL}/api/admin/genitori/${idGenitore}/riattiva`, {
        method: 'PUT'
      });

      if (!res.ok) {
        const errResp = await res.json().catch(() => ({}));
        throw new Error(errResp.message || `Errore HTTP ${res.status}`);
      }

      setGenitoreInModifica(null);
      await ricaricaLista();
      setMessage('✅ Genitore riattivato.');
    } catch (err) {
      console.error('Errore riattivazione:', err);
      setMessage(`❌ ${err.message}`);
    }
  }

  const getNomeCategoria = useCallback(
    (genitore) =>
      categorie.find((c) => String(c.id) === String(genitore.idCategoriaFiglio))?.nome ||
      genitore.catDelFiglio ||
      '-',
    [categorie]
  );

  const isAttivo = (stato) => String(stato || '').toUpperCase() === 'ATTIVO';

  const genitoriOrdinati = useMemo(() => {
    return [...genitori].sort((a, b) => {
      const keyA = `${a.cognome || ''} ${a.nome || ''}`.trim();
      const keyB = `${b.cognome || ''} ${b.nome || ''}`.trim();
      return keyA.localeCompare(keyB, 'it', { sensitivity: 'base' });
    });
  }, [genitori]);

  const genitoreOptions = useMemo(() => {
    return genitoriOrdinati.map((g) => ({
      value: String(g.id),
      label: `${g.cognome || ''} ${g.nome || ''}${g.email ? ` - ${g.email}` : ''}`.trim(),
      genitore: g
    }));
  }, [genitoriOrdinati]);

  const selectedGenitoreOption = useMemo(() => {
    if (!genitoreInModifica) return null;
    return (
      genitoreOptions.find((opt) => String(opt.value) === String(genitoreInModifica)) || null
    );
  }, [genitoreOptions, genitoreInModifica]);

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: 46,
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.12)',
      boxShadow: 'none',
      background: 'rgba(255,255,255,0.9)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      '&:hover': {
        border: '1px solid rgba(255,255,255,0.18)'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#666'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#222'
    }),
    input: (base) => ({
      ...base,
      color: '#222'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 14,
      overflow: 'hidden',
      zIndex: 30
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? 'rgba(53, 6, 209, 0.08)' : '#ffffff',
      color: '#222',
      cursor: 'pointer'
    })
  };

  const genitoriDaMostrare = useMemo(() => {
    if (!genitoreInModifica) return [];
    return genitoriOrdinati.filter(
      (g) => String(g.id) === String(genitoreInModifica)
    );
  }, [genitoriOrdinati, genitoreInModifica]);

  return (
    <div className="agggen-form-container" translate="no">
      <h2 className="agggen-form-title">Aggiorna Genitori</h2>

      {message && (
        <div
          className={`agggen-message-card ${
            message.startsWith('✅') ? 'agggen-success' : 'agggen-error'
          }`}
        >
          {message}
        </div>
      )}

      <div className="agggen-filtro-box">
        <div className="agggen-filtri-grid">
          <div className="agggen-form-group">
            <label className="agggen-label">Categoria</label>
            <select
              className="agggen-select"
              value={categoriaSelezionata}
              onChange={(e) => setCategoriaSelezionata(e.target.value)}
              disabled={loadingCategorie}
            >
              <option value="">
                {loadingCategorie ? 'Caricamento categorie...' : 'Seleziona una categoria'}
              </option>
              {categorie.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="agggen-form-group">
            <label className="agggen-label">Stato</label>
            <select
              className="agggen-select"
              value={statoFiltro}
              onChange={(e) => setStatoFiltro(e.target.value)}
            >
              <option value="ATTIVO">ATTIVO</option>
              <option value="INATTIVO">INATTIVO</option>
            </select>
          </div>

          <div className="agggen-form-group">
            <label className="agggen-label">Genitore</label>
            <div className="agggen-select-shell">
              <Select
                classNamePrefix="agggen-react-select"
                options={genitoreOptions}
                value={selectedGenitoreOption}
                onChange={(selectedOption) =>
                  handleEditClick(selectedOption ? selectedOption.genitore : null)
                }
                placeholder={
                  categoriaSelezionata
                    ? 'Cerca e seleziona un genitore...'
                    : 'Seleziona prima una categoria'
                }
                isClearable
                isSearchable
                noOptionsMessage={() => 'Nessun genitore trovato'}
                styles={selectStyles}
                isDisabled={!categoriaSelezionata || loadingGenitori}
              />
            </div>
          </div>
        </div>
      </div>

      {!categoriaSelezionata && (
        <div className="agggen-empty-state">
          <p>Seleziona una categoria per visualizzare i genitori.</p>
        </div>
      )}

      {categoriaSelezionata && loadingGenitori && (
        <div className="agggen-empty-state">
          <p>Caricamento genitori...</p>
        </div>
      )}

      {categoriaSelezionata && !loadingGenitori && genitoriOrdinati.length === 0 && (
        <div className="agggen-empty-state">
          <p>Nessun genitore presente per i filtri selezionati.</p>
        </div>
      )}

      {categoriaSelezionata && !genitoreInModifica && (
        <div className="agggen-empty-state">
          <p>Seleziona un genitore dalla lista.</p>
        </div>
      )}

      {genitoriDaMostrare.length > 0 && (
        <div className="agggen-list">
          {genitoriDaMostrare.map((g) => (
            <article className="agggen-card" key={g.id}>
              <div className="agggen-card-header">
                <div className="agggen-card-title">
                  <h3>
                    {g.cognome} {g.nome}
                  </h3>
                  <p>{g.email || '-'}</p>
                </div>

                <div className="agggen-card-badges">
                  <span className="agggen-badge">{getNomeCategoria(g)}</span>
                  <span className={`agggen-badge ${isAttivo(g.stato) ? 'attivo' : 'inattivo'}`}>
                    {g.stato || '-'}
                  </span>
                </div>
              </div>

              <div className="agggen-card-info">
                <div className="agggen-info-item">
                  <span className="agggen-info-label">Telefono</span>
                  <span className="agggen-info-value">{g.telefono || '-'}</span>
                </div>

                <div className="agggen-info-item">
                  <span className="agggen-info-label">Indirizzo</span>
                  <span className="agggen-info-value">{g.indirizzo || '-'}</span>
                </div>
              </div>

              <div className="agggen-card-actions">
                <button className="agggen-modifica" type="button" onClick={() => handleEditClick(g)}>
                  Modifica
                </button>
              </div>

              {genitoreInModifica === g.id && (
                <div className="agggen-espansione-card">
                  <h3 className="agggen-edit-title">Modifica genitore</h3>

                  <div className="agggen-edit-grid">
                    <div className="agggen-edit-group">
                      <label className="agggen-label">Nome</label>
                      <input
                        className="agggen-input uppercase"
                        name="nome"
                        value={formEdit.nome}
                        onChange={handleEditChange}
                      />
                    </div>

                    <div className="agggen-edit-group">
                      <label className="agggen-label">Cognome</label>
                      <input
                        className="agggen-input uppercase"
                        name="cognome"
                        value={formEdit.cognome}
                        onChange={handleEditChange}
                      />
                    </div>

                    <div className="agggen-edit-group">
                      <label className="agggen-label">Telefono</label>
                      <input
                        className="agggen-input"
                        name="telefono"
                        value={formEdit.telefono}
                        onChange={handleEditChange}
                      />
                    </div>

                    <div className="agggen-edit-group">
                      <label className="agggen-label">Email</label>
                      <input
                        className="agggen-input"
                        name="email"
                        type="email"
                        value={formEdit.email}
                        onChange={handleEditChange}
                      />
                    </div>

                    <div className="agggen-edit-group">
                      <label className="agggen-label">Indirizzo</label>
                      <input
                        className="agggen-input uppercase"
                        name="indirizzo"
                        value={formEdit.indirizzo}
                        onChange={handleEditChange}
                      />
                    </div>

                    <div className="agggen-edit-group">
                      <label className="agggen-label">Categoria del figlio</label>
                      <select
                        className="agggen-select"
                        name="idCategoriaFiglio"
                        value={formEdit.idCategoriaFiglio}
                        onChange={handleEditChange}
                      >
                        <option value="">Seleziona una categoria</option>
                        {categorie.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="agggen-bottoni-espansi">
                    <button className="agggen-salva" type="button" onClick={() => handleSave(g.id)}>
                      Salva
                    </button>

                    <button
                      className="agggen-annulla"
                      type="button"
                      onClick={() => setGenitoreInModifica(null)}
                    >
                      Annulla
                    </button>

                    {isAttivo(g.stato) ? (
                      <button
                        className="agggen-disattiva"
                        type="button"
                        onClick={() => handleDisattiva(g.id)}
                      >
                        Disattiva
                      </button>
                    ) : (
                      <button
                        className="agggen-riattiva"
                        type="button"
                        onClick={() => handleRiattiva(g.id)}
                      >
                        Riattiva
                      </button>
                    )}
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="agggen-button-row">
        <button
          className="agggen-custom-button"
          type="button"
          onClick={() => navigate('/gestionale')}
        >
          Torna al Gestionale
        </button>
      </div>
    </div>
  );
}